import React, { useCallback, useEffect, useMemo } from 'react'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import { getComparator, stableSort } from './logic'
import { DataTableButton } from './DataTableButton'
import { DataItemAction, DataTableProps, DataTableRow } from './DataTableTypes'
import { DataTableHead } from './DataTableHead'
import { DataTableRowCells } from './DataTableRowCells'
import { DataTableCheckbox } from './DataTableCheckbox'

const filterRows: (
  r: DataTableRow[],
  filterString: string | undefined,
  filterColumn: number | undefined,
) => DataTableRow[] = (r: DataTableRow[], filterString: string | undefined, filterColumn: number | undefined) => {
  if (!filterString) {
    return r
  }

  if (filterColumn) {
    return r.filter((row) => row.values[filterColumn].toLowerCase().includes(filterString.toLowerCase()))
  }

  return r.filter(
    (row) => row.values.findIndex((v) => v.toLowerCase().includes((filterString as string).toLowerCase())) >= 0,
  )
}

export const DataTable: React.FC<DataTableProps> = React.memo(
  ({
    rows,
    columns,
    filterColumn,
    filterString,
    onButtonClicked,
    allowedActions,
    autoselect,
    onSelectionChanged,
    disableHover,
    onSortingChanged,
  }) => {
    const selected = React.useRef<string[]>([])
    const [selectedAll, setSelectedAll] = React.useState<boolean>(false)
    const [sortedRows, setSortedRows] = React.useState<DataTableRow[]>(rows)

    const appliedFilterString = useMemo<string>(() => filterString ?? '', [filterString])

    const nRows = useMemo(() => {
      return rows.length
    }, [rows])

    useEffect(() => {
      const filtered = filterRows(rows, appliedFilterString, filterColumn)
      if (
        !allowedActions?.sortable ||
        filtered
          .map((f) => f.id)
          .sort()
          .join(',') !==
          sortedRows
            .map((s) => s.id)
            .sort()
            .join(',')
      ) {
        setSortedRows(filtered)
        if (onSortingChanged) {
          onSortingChanged(filtered.map((f) => f.id))
        }
      }
    }, [appliedFilterString, sortedRows, rows, filterColumn, onSortingChanged, allowedActions?.sortable])

    // when data changes, per default, select everything
    useEffect(() => {
      if (autoselect) {
        selected.current = rows.map((r) => r.id)
      }
    }, [rows, autoselect])

    const handleSelectAllClick = useCallback(
      (doSelect: boolean): void => {
        const s = doSelect ? rows.map((r) => r.id) : []
        selected.current = s
        setSelectedAll((prev) => !prev)

        if (onSelectionChanged) {
          onSelectionChanged(s)
        }
      },
      [onSelectionChanged, selected, rows],
    )

    const buttonClicked = useCallback(
      (id: string, action: DataItemAction): void => {
        if (onButtonClicked) {
          onButtonClicked(id, action)
        }
      },
      [onButtonClicked],
    )

    const editClicked = useCallback((id: string) => buttonClicked(id, 'edit'), [buttonClicked])
    const restoreClicked = useCallback((id: string) => buttonClicked(id, 'restore'), [buttonClicked])
    const generateClicked = useCallback((id: string) => buttonClicked(id, 'generate'), [buttonClicked])
    const viewClicked = useCallback((id: string) => buttonClicked(id, 'view'), [buttonClicked])
    const grantLicenseClicked = useCallback((id: string) => buttonClicked(id, 'grantLicense'), [buttonClicked])
    const batchGrantLicenseClicked = useCallback((id: string) => buttonClicked(id, 'batchGrantLicense'), [
      buttonClicked,
    ])
    const createOrderClicked = useCallback((id: string) => buttonClicked(id, 'createOrder'), [buttonClicked])
    const deleteClicked = useCallback((id: string) => buttonClicked(id, 'delete'), [buttonClicked])

    const handleSelectClick = useCallback(
      (id: string): void => {
        const selectedIndex = selected.current.indexOf(id)
        let newSelected: string[] = []

        if (selectedIndex === -1) {
          newSelected = newSelected.concat(selected.current, id)
        } else if (selectedIndex === 0) {
          newSelected = newSelected.concat(selected.current.slice(1))
        } else if (selectedIndex === selected.current.length - 1) {
          newSelected = newSelected.concat(selected.current.slice(0, -1))
        } else if (selectedIndex > 0) {
          newSelected = newSelected.concat(
            selected.current.slice(0, selectedIndex),
            selected.current.slice(selectedIndex + 1),
          )
        }

        if (onSelectionChanged) {
          onSelectionChanged(newSelected)
        }
        selected.current = newSelected
      },
      [onSelectionChanged, selected],
    )

    const doSort = (col: number, order: string): void => {
      const sRows = filterRows(
        stableSort(rows, getComparator(order === 'asc' ? 'asc' : 'desc', col, columns[col].numeric || false)),
        appliedFilterString,
        filterColumn,
      )
      setSortedRows(sRows)
      if (onSortingChanged) {
        onSortingChanged(sRows.map((f) => f.id))
      }
    }

    return (
      <TableContainer>
        <Table>
          <DataTableHead
            doSort={doSort}
            sortable={allowedActions?.sortable}
            columns={columns}
            onSelectAllClick={handleSelectAllClick}
            checkable={allowedActions?.checkable}
          />
          <TableBody>
            {sortedRows.map((row, i: number) => (
              <TableRow hover={!disableHover} key={row.id + i}>
                {allowedActions?.checkable && (
                  <DataTableCheckbox
                    filterString={appliedFilterString}
                    nRows={nRows}
                    selectedAll={selectedAll}
                    checkboxClicked={handleSelectClick}
                    id={row.id}
                  />
                )}
                <DataTableRowCells row={row} columns={columns} />
                <TableCell align="right">
                  <DataTableButton
                    condition={allowedActions?.licenseGrantable}
                    title={'licenses.grantLicense'}
                    action={'grantLicense'}
                    onButtonClicked={grantLicenseClicked}
                    id={row.id}
                  />
                  <DataTableButton
                    condition={allowedActions?.createOrder}
                    title={'actions.createOrder'}
                    action={'createOrder'}
                    onButtonClicked={createOrderClicked}
                    id={row.id}
                  />
                  <DataTableButton
                    condition={allowedActions?.batchGrantLicense}
                    title={'licenses.batchGrantLicense'}
                    action={'batchGrantLicense'}
                    onButtonClicked={batchGrantLicenseClicked}
                    id={row.id}
                  />
                  <DataTableButton
                    condition={allowedActions?.editable}
                    title={'actions.edit'}
                    action={'edit'}
                    onButtonClicked={editClicked}
                    id={row.id}
                  />
                  <DataTableButton
                    condition={allowedActions?.restorable}
                    title={'actions.restore'}
                    action={'restore'}
                    onButtonClicked={restoreClicked}
                    id={row.id}
                  />
                  <DataTableButton
                    condition={allowedActions?.deletable}
                    title={'actions.delete'}
                    action={'delete'}
                    onButtonClicked={deleteClicked}
                    id={row.id}
                  />
                  <DataTableButton
                    condition={allowedActions?.generatable}
                    title={'actions.generate'}
                    action={'generate'}
                    onButtonClicked={generateClicked}
                    id={row.id}
                  />
                  <DataTableButton
                    condition={allowedActions?.viewable}
                    title={'actions.view'}
                    action={'view'}
                    onButtonClicked={viewClicked}
                    id={row.id}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  },
)
DataTable.displayName = 'DataTable'

export default DataTable
